import type {
  PermissionTreeItem,
  PermissionTreeOrigin,
  ResourceType
} from '@/Network/Types/Responses/PermissionResponses'

import { getNodeKey, isLeafNode } from './utils'

export type OriginFilter = 'all' | 'direct' | 'group'

export interface FlatTreeRow {
  kind: 'node' | 'empty'
  /** getNodeKey encadeado (nós) ou `${parentKey}-empty` (linha vazia). */
  key: string
  depth: number
  node?: PermissionTreeItem
  hasChildren?: boolean
  open?: boolean
  /** Ancestral exibido só como contexto de um match (sem match próprio). */
  dimmed?: boolean
  /** Lista COMPLETA de origens do nó, na ordem do backend (direct primeiro). */
  origins?: PermissionTreeOrigin[]
  /** Só em rows `empty`: tipo do nó pai, para o texto do vazio variar por nível. */
  parentType?: ResourceType
}

export interface FlattenOptions {
  query: string
  origin: OriginFilter
  expandedKeys: Record<string, boolean>
}

/** Normaliza p/ comparação case-insensitive e accent-insensitive (NFD + strip de diacríticos). */
export const normalizeText = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * Achata a árvore de permissões na lista de rows renderizada pelo modal,
 * aplicando busca (nome ou ID) + filtro de origem:
 * - um nó entra se ELE casa com os filtros ou se algum descendente casa; o
 *   ancestral sem match próprio entra `dimmed` (contexto do match);
 * - o filtro de origem casa se QUALQUER origem do nó tem o kind filtrado (nó
 *   misto direct+grupo aparece nos dois filtros);
 * - com filtro ativo a expansão é forçada (forceOpen); sem filtro vale
 *   `expandedKeys`;
 * - nó aberto sem filhos e SEM filtro ativo emite uma row `empty` (depth+1);
 * - cada row de nó carrega a lista completa de `origins` vinda do backend.
 */
export function flattenPermissionsTree(
  nodes: PermissionTreeItem[],
  opts: FlattenOptions
): FlatTreeRow[] {
  const query = opts.query.trim()
  const normalizedQuery = normalizeText(query)
  const filterActive = query.length > 0 || opts.origin !== 'all'

  const matchSelf = (node: PermissionTreeItem): boolean => {
    const matchesQuery =
      normalizedQuery.length === 0 ||
      normalizeText(node.resource_name).includes(normalizedQuery) ||
      String(node.resource_id).includes(normalizedQuery)
    const matchesOrigin =
      opts.origin === 'all' || node.origins.some((origin) => origin.kind === opts.origin)
    return matchesQuery && matchesOrigin
  }

  const isSubtreeVisible = (node: PermissionTreeItem): boolean => {
    if (matchSelf(node)) return true
    return (node.children ?? []).some((child) => isSubtreeVisible(child))
  }

  const rows: FlatTreeRow[] = []

  const walk = (
    levelNodes: PermissionTreeItem[],
    parentKey: string | undefined,
    depth: number
  ): void => {
    for (const node of levelNodes) {
      if (filterActive && !isSubtreeVisible(node)) continue

      const selfMatches = matchSelf(node)

      const key = getNodeKey(node, parentKey)
      const hasChildren = !isLeafNode(node)
      const open = hasChildren && (filterActive || Boolean(opts.expandedKeys[key]))

      rows.push({
        kind: 'node',
        key,
        depth,
        node,
        hasChildren,
        open,
        dimmed: !selfMatches,
        origins: node.origins
      })

      if (!open) continue

      if (node.children?.length) {
        walk(node.children, key, depth + 1)
      } else if (!filterActive) {
        rows.push({
          kind: 'empty',
          key: `${key}-empty`,
          depth: depth + 1,
          parentType: node.resource_type
        })
      }
    }
  }

  walk(nodes, undefined, 0)
  return rows
}

/**
 * Conta TODOS os nós carregados (recursivamente) por origem — alimenta os
 * contadores do segmented (`Todas` / `Diretas` / `Via grupo`):
 * - `all` = nº total de nós;
 * - `direct` = nós com ALGUMA origem direct;
 * - `group` = nós com ALGUMA origem group.
 * Nó misto (direct + grupo) conta em `direct` E em `group` — por isso
 * `direct + group` pode ser maior que `all`.
 */
export function countPermissionsByOrigin(nodes: PermissionTreeItem[]): {
  all: number
  direct: number
  group: number
} {
  let all = 0
  let direct = 0
  let group = 0

  const walk = (levelNodes: PermissionTreeItem[]): void => {
    for (const node of levelNodes) {
      all += 1
      if (node.origins.some((origin) => origin.kind === 'direct')) direct += 1
      if (node.origins.some((origin) => origin.kind === 'group')) group += 1
      if (node.children?.length) walk(node.children)
    }
  }

  walk(nodes)
  return { all, direct, group }
}
